/**
 * Phone number utility functions and constants
 */

/**
 * Country codes for phone number input - maps country code to dial code
 */
export const PHONE_COUNTRY_CODES: Record<string, PhoneCountryCodeDigits> = {
    US: "+1",
    CA: "+1",
    GB: "+44",
    AU: "+61",
    NZ: "+64",
} as const;
/** Unique country codes, longest first, so parsePhoneForEdit matches e.g. +44 before +1. */
const COUNTRY_CODE_DIGITS: PhoneCountryCodeDigits[] = Array.from(new Set(Object.values(PHONE_COUNTRY_CODES))).sort(
    (a, b) => b.length - a.length,
);

export type PhoneCountryCode = keyof typeof PHONE_COUNTRY_CODES;
export type PhoneCountryCodeDigits = `+${number}`;

/** Max digits for local part (after country code). US/CA use 10; others allow up to 12 for international. */
const MAX_LOCAL_DIGITS_OTHER = 12;

/** Min digits for local part by country. AU: 9, NZ: 8, US/CA/GB: 10. */
const MIN_LOCAL_DIGITS_BY_COUNTRY: Partial<Record<PhoneCountryCodeDigits, number>> = {
    "+1": 10,
    "+44": 10,
    "+61": 9,
    "+64": 8,
};

/**
 * Returns the maximum number of digits allowed for the local part (after country code).
 * US/CA: 10; GB, AU, NZ: 12 to support full national numbers.
 */
export function getMaxLocalDigits(countryCode: PhoneCountryCodeDigits): number {
    return countryCode === "+1" ? 10 : MAX_LOCAL_DIGITS_OTHER;
}

/**
 * Returns the minimum number of digits required for the local part (after country code).
 * US/CA/GB: 10; AU: 9; NZ: 8. Unknown codes default to 10 for backward compatibility.
 */
export function getMinLocalDigits(countryCode: PhoneCountryCodeDigits): number {
    return MIN_LOCAL_DIGITS_BY_COUNTRY[countryCode] ?? 10;
}

/**
 * Formats local digits for phone input: US/CA get (XXX) XXX-XXXX; other countries get raw digits.
 * Callers should truncate rawValue to getMaxLocalDigits(countryCode) before passing.
 */
export function formatLocalPhoneInput(countryCode: PhoneCountryCodeDigits, rawValue: string): string {
    if (countryCode === "+1") {
        return formatPhoneInput(rawValue.slice(0, 10));
    }
    return rawValue;
}

/**
 * Formats a phone number as the user types for input fields.
 * Formats as (XXX) XXX-XXXX
 * @param value - Raw input value
 * @returns Formatted phone number string
 */
export function formatPhoneInput(value: string): string {
    const digits = value.replace(/[^0-9]/g, "");

    if (digits.length === 0) return "";
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Formats a phone number for display with country code.
 * Formats as +1 (XXX) XXX-XXXX
 * @param countryCode - Country code (e.g., "+1", "+44", "+61", "+64")
 * @param phoneNumber - Phone number (can include non-digit characters)
 * @returns Formatted phone number string with country code
 */
export function formatPhoneForDisplay(phoneNumber: string): string {
    const digits = phoneNumber.replace(/\D/g, "");

    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length > 10 && digits.length <= 15) {
        return `+${digits.slice(0, -10)} (${digits.slice(-10, -7)}) ${digits.slice(-7, -4)}-${digits.slice(-4)}`;
    }

    return phoneNumber;
}

/**
 * Formats a dialed number for display in the dialer.
 * Formats as +1 (XXX) XXX-XXXX with progressive formatting as digits are added.
 * @param number - Dialed number digits
 * @returns Formatted dialed number string
 */
export function formatDialedNumber(number: string): string {
    const digits = number.replace(/[^0-9]/g, "");

    if (digits.length === 0) return "";
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    if (digits.length === 11)
        return `+${digits.slice(0, 1)} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    if (digits.length === 12)
        return `+${digits.slice(0, 2)} (${digits.slice(2, 5)}) ${digits.slice(5, 8)}-${digits.slice(8)}`;
    return `+${digits.slice(0, 3)} (${digits.slice(3, 6)}) ${digits.slice(6, 9)}-${digits.slice(9)}`;
}

/**
 * Extracts only digits from a phone number string
 * @param phoneNumber - Phone number that may contain formatting characters
 * @returns Only the digits from the phone number
 */
export function extractDigits(phoneNumber: string): string {
    return phoneNumber.replace(/\D/g, "");
}

/**
 * Sanitizes raw phone input (typed or pasted) into a formatted local phone string.
 *
 * Handles pasted numbers that include a country/trunk code (e.g. "+1 (804) 222-1111" or
 * "18042221111" when dial code is "+1") by stripping the leading country-code digits when
 * the digit string exceeds max AND starts with the country code's numeric prefix.
 * Otherwise clamps to the first `max` digits so over-long typed input is handled identically
 * to the previous behaviour.
 *
 * @param rawInput - The raw string from the input element (may include formatting chars)
 * @param dialCode - The currently selected dial code (e.g. "+1", "+44")
 * @returns A formatted local phone string suitable for the input field
 */
export function sanitizeLocalPhoneInput(rawInput: string, dialCode: PhoneCountryCodeDigits): string {
    const digits = rawInput.replace(/\D/g, "");
    const max = getMaxLocalDigits(dialCode);
    const codeDigits = dialCode.replace("+", "");

    // +1 (NANP) local numbers can never start with "1" (area codes never start with 0 or 1), so
    // any excess-length input starting with "1" is unambiguously country-code-prefixed regardless
    // of how many extra digits follow. Multi-digit codes (+44/+61/+64) don't have that guarantee —
    // a legitimate local number CAN start with the same digits as the dial code — so for those,
    // only an exact length match (code + max) is unambiguous; a longer input is left as-is rather
    // than guessed at.
    const hasUnambiguousCodePrefix =
        dialCode === "+1" ? digits.length > max : digits.length === max + codeDigits.length;

    let local = digits;
    if (hasUnambiguousCodePrefix && digits.startsWith(codeDigits)) {
        local = digits.slice(codeDigits.length);
    }

    return formatLocalPhoneInput(dialCode, local.slice(0, max));
}

/**
 * Validates a phone number (local part only). When countryCode is provided, requires digit count
 * between getMinLocalDigits and getMaxLocalDigits for that country (e.g. AU 9, NZ 8–9, US/CA 10).
 * When countryCode is omitted, requires at least 10 digits for backward compatibility.
 */
export function isValidPhoneNumber(phoneNumber: string, countryCode?: PhoneCountryCodeDigits): boolean {
    const len = extractDigits(phoneNumber).length;
    if (countryCode !== undefined) {
        return len >= getMinLocalDigits(countryCode) && len <= getMaxLocalDigits(countryCode);
    }
    return len >= 10;
}

/**
 * Formats local digits for edit-form display. Only +1 (US/CA) uses (XXX) XXX-XXXX;
 * other country codes get raw digits to avoid truncation or misleading formatting.
 */
function formatLocalForEdit(countryCode: PhoneCountryCodeDigits, localDigits: string): string {
    if (countryCode === "+1" && localDigits.length <= 10) {
        return formatPhoneInput(localDigits);
    }
    return localDigits;
}

/**
 * Returns a country key for a dial code. When multiple keys share a dial code (e.g. US and CA for +1),
 * returns the first match so edit forms have a stable default; callers should store the key, not the dial code.
 */
export function getDefaultCountryKeyForDialCode(dial: PhoneCountryCodeDigits): PhoneCountryCode {
    const key = Object.entries(PHONE_COUNTRY_CODES).find(([, v]) => v === dial)?.[0];
    return (key as PhoneCountryCode) ?? "US";
}

/**
 * Parses a full E.164-style phone number (e.g. +15557779999) into country code and local part
 * for use in edit forms. For +1 (US/CA), localFormatted is (XXX) XXX-XXXX when 10 digits;
 * for other country codes, localFormatted is raw digits to avoid truncation or wrong formatting.
 * @param fullPhone - Full phone number with country code
 * @returns { countryCode, localFormatted } for pre-filling the country selector and local input
 */
export function parsePhoneForEdit(fullPhone: string): {
    countryCode: PhoneCountryCodeDigits;
    localFormatted: string;
} {
    const digits = fullPhone.replace(/\D/g, "");
    for (const code of COUNTRY_CODE_DIGITS) {
        const codeDigits = code.replace("+", "");
        if (digits.startsWith(codeDigits)) {
            const local = digits.slice(codeDigits.length);
            const countryCode = code as PhoneCountryCodeDigits;
            return {
                countryCode,
                localFormatted: formatLocalForEdit(countryCode, local),
            };
        }
    }
    const fallback = digits.length >= 10 ? digits.slice(-10) : digits;
    return {
        countryCode: "+1",
        localFormatted: formatLocalForEdit("+1", fallback),
    };
}
